import React from 'react';
import { FiUsers, FiMapPin, FiCalendar, FiFileText, FiZap, FiDollarSign, FiGift } from 'react-icons/fi';
import dayjs from 'dayjs';

const INTENSIDAD = {
  baja:     { label: 'Baja',     color: '#0047bb', bg: '#eff6ff', dot: '🟢' },
  moderada: { label: 'Moderada', color: '#d97706', bg: '#fffbeb', dot: '🟡' },
  alta:     { label: 'Alta',     color: '#dc2626', bg: '#fef2f2', dot: '🔴' },
};

const InfoRow = ({ icon, label, children }) => (
  <div className="flex items-start gap-3 py-3.5 border-b border-gray-100 last:border-0">
    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: '#eff6ff' }}>
      <span className="text-blue-600">{icon}</span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">{label}</p>
      <div className="text-[14px] font-semibold text-gray-800">{children}</div>
    </div>
  </div>
);

const PlanCard = ({ plan }) => (
  <div className="nd-card p-4 border-l-4 border-blue-500">
    <div className="flex justify-between items-start gap-4">
      <div>
        <p className="font-bold text-gray-800 capitalize text-sm">{plan.plan}</p>
        {plan.matricula > 0 && (
          <p className="text-[11px] text-gray-400 mt-0.5">+ Matrícula ${plan.matricula.toLocaleString('es-CL')}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-lg font-extrabold text-blue-600">${plan.valorParticular?.toLocaleString('es-CL')}</p>
        {plan.valorVecino > 0 && (
          <p className="text-[11px] font-semibold text-blue-500">Vecino: ${plan.valorVecino.toLocaleString('es-CL')}</p>
        )}
        {plan.valorAdultoMayor > 0 && (
          <p className="text-[11px] font-semibold text-purple-500">Adulto mayor: ${plan.valorAdultoMayor.toLocaleString('es-CL')}</p>
        )}
      </div>
    </div>
  </div>
);

export default function TallerInfo({ taller, esGratis }) {
  const intensidad = INTENSIDAD[taller?.nivelIntensidad] || INTENSIDAD.moderada;
  const tieneImg   = !!taller?.imagen?.url;
  const tieneVentana = !!(taller?.inscripcionInicio || taller?.inscripcionFin);
  const tieneRango   = !!(taller?.actividadesInicio && taller?.actividadesFin);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">

      {/* ── Info principal (2/3) ── */}
      <div className="lg:col-span-2 nd-card p-0 overflow-hidden">
        <div className="px-5 pt-5 pb-1">
          <h2 className="text-lg font-extrabold text-gray-900 mb-0">Información del taller</h2>
        </div>
        <div className="px-5">
          {taller.edadMin !== undefined && (
            <InfoRow icon={<FiUsers size={16} />} label="Edades">
              {taller.edadMin && taller.edadMax
                ? `${taller.edadMin} – ${taller.edadMax} años`
                : taller.edadMin
                  ? `Desde ${taller.edadMin} años`
                  : taller.edadMax
                    ? `Hasta ${taller.edadMax} años`
                    : 'Todas las edades'}
            </InfoRow>
          )}

          <InfoRow icon={<FiMapPin size={16} />} label="Recinto">
            <span>{taller.recinto?.nombre || '—'}</span>
            {taller.recinto?.direccion && (
              <p className="text-[12px] text-gray-400 font-normal mt-0.5">{taller.recinto.direccion}</p>
            )}
          </InfoRow>

          {taller.descripcion && (
            <InfoRow icon={<FiFileText size={16} />} label="Descripción">
              <p className="text-sm font-normal text-gray-600 leading-relaxed">{taller.descripcion}</p>
            </InfoRow>
          )}

          {tieneVentana && (
            <InfoRow icon={<FiCalendar size={16} />} label="Período de inscripción">
              <div className="flex flex-wrap gap-4 text-sm">
                {taller.inscripcionInicio && (
                  <div>
                    <span className="text-[11px] text-gray-400 block">Apertura</span>
                    {dayjs(taller.inscripcionInicio).format('DD/MM/YYYY HH:mm')}
                  </div>
                )}
                {taller.inscripcionFin && (
                  <div>
                    <span className="text-[11px] text-gray-400 block">Cierre</span>
                    {dayjs(taller.inscripcionFin).format('DD/MM/YYYY HH:mm')}
                  </div>
                )}
              </div>
            </InfoRow>
          )}

          {tieneRango && (
            <InfoRow icon={<FiCalendar size={16} />} label="Duración de actividades">
              {dayjs(taller.actividadesInicio).format('DD/MM/YYYY')} → {dayjs(taller.actividadesFin).format('DD/MM/YYYY')}
            </InfoRow>
          )}

          <InfoRow icon={<FiZap size={16} />} label="Intensidad">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold"
              style={{ background: intensidad.bg, color: intensidad.color }}>
              {intensidad.dot} {intensidad.label}
            </span>
          </InfoRow>
        </div>
        <div className="h-4" />
      </div>

      {/* ── Columna lateral (1/3) ── */}
      <div className="flex flex-col gap-4">
        {/* Imagen */}
        {tieneImg && (
          <div className="nd-card overflow-hidden p-0">
            <div style={{ position: 'relative', paddingBottom: '62%', background: '#f3f4f6' }}>
              <img
                src={taller.imagen.url} alt={taller.nombre}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          </div>
        )}

        {/* Precios */}
        <div className="nd-card p-5">
          <div className="flex items-center gap-2 mb-4">
            {esGratis ? <FiGift size={18} className="text-blue-600" /> : <FiDollarSign size={18} className="text-blue-600" />}
            <h3 className="font-extrabold text-gray-900 text-sm">
              {esGratis ? 'Este taller es gratuito' : 'Planes disponibles'}
            </h3>
          </div>

          {esGratis ? (
            <div className="rounded-2xl p-4 text-center" style={{ background: '#eff6ff' }}>
              <p className="text-3xl mb-2">🎁</p>
              <p className="font-bold text-blue-700">¡Sin costo!</p>
              <p className="text-sm text-blue-600 mt-1">Solo confirma tu inscripción</p>
            </div>
          ) : (
            <div className="space-y-3">
              {taller.modalidadPago?.map((plan, i) => <PlanCard key={i} plan={plan} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
